import { toast } from "@suraasa/placebo-ui"
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import {
  PreferredPromptData,
  QuizForm,
  QuizGenerateProResponse,
  QuizGenerateResponse,
  QuizProForm,
  QuizPromptData,
  ToolType,
} from "api/resources/aiTools/types"
import { APIResponse } from "api/types"
import { APIError } from "api/utils"
import { getToolName } from "features/AItools/helper"
import {
  StreamFetchProps,
  StreamQuery,
  StreamResponseEnum,
  useStreamQuery,
} from "features/AItools/hooks/useStreamQuery"
import { useEffect, useState } from "react"
import { flushSync } from "react-dom"
import { UseFormSetError } from "react-hook-form"
import { useNavigate, useSearchParams } from "react-router-dom"
import routes from "routes"
import { mapErrors } from "utils/helpers"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

export type QuizReturnType = {
  currentResponseId: number | null
  setCurrentResponseId: (id: number | null) => void
  promptDataId: string | number | null
  setPromptDataId: React.Dispatch<React.SetStateAction<string | number | null>>
  responseIds: number[] | null
  setResponseIds: React.Dispatch<React.SetStateAction<number[] | null>>
  overviewData: QuizPromptData | undefined

  userVote: UseMutationResult<
    never,
    unknown,
    {
      reaction: boolean | undefined | null
      isPro: boolean
    },
    unknown
  >
  output: string | undefined
  isLoading: boolean
  stream: Omit<StreamQuery, "refetchAsync"> & {
    refetchAsync: <T>(
      props: Omit<StreamFetchProps<T>, "toolType">
    ) => Promise<void>
  }
  streamPro: Omit<StreamQuery, "refetchAsync"> & {
    refetchAsync: <T>(
      props: Omit<StreamFetchProps<T>, "toolType">
    ) => Promise<void>
  }

  regenerateStream: StreamQuery
  generateTool: UseMutationResult<
    APIResponse<QuizGenerateResponse>,
    any,
    {
      data: QuizForm
      setError: UseFormSetError<QuizForm>
    },
    unknown
  >
  generateProTool: UseMutationResult<
    APIResponse<QuizGenerateProResponse>,
    any,
    {
      data: QuizProForm
      setError: UseFormSetError<QuizProForm>
    },
    unknown
  >
  regenerateResponse: UseMutationResult<
    APIResponse<{
      id: number
    }>,
    unknown,
    {
      reason: string
      isPro: boolean
    },
    unknown
  >
  isPositiveResponse: boolean | null | undefined
  resetState: () => void
  pageLoading: boolean
  onBack: () => void
  handleCancelApi: () => void
  formData: {
    basicForm?: QuizForm
    proForm?: QuizProForm
  } | null
  preferredPromptData: PreferredPromptData | null
  isError: boolean
}

export const useQuiz = (): QuizReturnType => {
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")
  const navigate = useNavigate()

  const [overviewData, setOverviewData] = useState<QuizPromptData | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const [isPositiveResponse, setIsPositiveResponse] = useState<
    boolean | null | undefined
  >()
  const [currentResponseId, setCurrentResponseId] = useState<number | null>(
    null
  )
  const [promptDataId, setPromptDataId] = useState<string | number | null>(id)
  const [responseIds, setResponseIds] = useState<number[] | null>(null)
  const [formData, setFormData] = useState<{
    basicForm?: QuizForm
    proForm?: QuizProForm
  } | null>(null)
  const [output, setOutput] = useState<string>("")
  const [pageLoading, setPageLoading] = useState(false)
  const [currentMode, setCurrentMode] = useState<"basic" | "pro" | null>(null)

  const getContent = async (id: number) => {
    // resetStream()
    setOutput("")
    setIsLoading(true)
    const res = await api.aiTools.quiz.retrieveContent({
      urlParams: {
        id: id,
      },
    })
    if (res instanceof APIError) {
      toast.error(res.message || "Something went wrong")
      setIsLoading(false)
      return
    }

    setIsPositiveResponse(res.isPositiveResponse)
    setOutput(res.output)
    setIsLoading(false)
    // resetStream()
  }

  const resetState = () => {
    setOutput("")
    setPromptDataId(null)
    setCurrentResponseId(null)
    setIsPositiveResponse(undefined)
    setOverviewData(undefined)
    setResponseIds(null)
  }

  const handleCancelApi = () => {
    if (streamTitle.isLoading) streamTitle.handleCancel()
    if (streamOutput.isLoading) streamOutput.handleCancel()
    if (regenerateStream.isLoading) regenerateStream.handleCancel()

    if (streamTitlePro.isLoading) streamTitlePro.handleCancel()
    if (streamOutputPro.isLoading) streamOutputPro.handleCancel()
    if (regenerateStreamPro.isLoading) regenerateStreamPro.handleCancel()
  }
  const onBack = () => {
    resetState()
    navigate(routes.aiTools.quiz, { replace: true })
    handleCancelApi()
  }
  // This flow is for when user clicks on history item
  // If that is the case then we just fetch the overview and the latest generated output
  useEffect(() => {
    if (id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPageLoading(true)
      api.aiTools.quiz
        .getPromptData({
          urlParams: {
            id: id!,
          },
        })
        .then(async data => {
          setPromptDataId(id)
          const { responseIds } = data
          if (data.isPro) {
            setCurrentMode("pro")
          } else {
            setCurrentMode("basic")
          }
          if (responseIds) {
            const [latestResponseId] = responseIds
            setCurrentResponseId(latestResponseId)
            setResponseIds(responseIds)
            await getContent(latestResponseId)
          }
          setOverviewData(data)
          setPageLoading(false)
        })
        .catch(error => {
          console.error("Error fetching prompt data:", error)
          setPageLoading(false)
        })
    }
  }, [id])

  const overviewAPI = async (id: string | number | null) => {
    const res = await api.aiTools.quiz.getPromptData({
      urlParams: {
        id: id!,
      },
    })
    if (res instanceof APIError) {
      toast.error("Prompt failed to generate")
      return
    }

    const { responseIds } = res

    if (responseIds) {
      const [latestResponseId] = responseIds
      setCurrentResponseId(latestResponseId)
      setResponseIds(responseIds)
    }
    if (res.isPro) {
      setCurrentMode("pro")
    } else {
      setCurrentMode("basic")
    }
    setOverviewData(res)
  }

  const generateTool = useMutation({
    mutationFn: async ({
      data,
    }: {
      data: QuizForm
      setError: UseFormSetError<QuizForm>
    }) => {
      setFormData(prevState => {
        if (!prevState) {
          return { basicForm: data }
        }

        return { ...prevState, basicForm: data }
      })
      setCurrentMode("basic")
      return api.aiTools.quiz.generate({ data })
    },
    onSuccess: async res => {
      const { responseId, id } = res
      resetState()
      flushSync(() => {
        setCurrentResponseId(responseId)
        setPromptDataId(id)
        setOverviewData({
          id: res.id,
          numberOfQuestions: res.numberOfQuestions,
          curriculum: res.curriculum,
          grade: res.grade,
          objective: res.objective,
          title: `New ${getToolName(ToolType.quiz)}`,
          isPro: false,
        })
      })

      await streamOutput.refetchAsync({
        currentResponseId: responseId,
      })
      await streamTitle.refetchAsync({
        currentResponseId: responseId,
      })
      try {
        await overviewAPI(id)
      } catch (error) {
        console.error("Error fetching overview data:", error)
      }
      queryClient.refetchQueries({
        queryKey: queries.aiTools.listHistory(ToolType.quiz).queryKey,
        type: "active",
        exact: true,
      })
    },
    onError: (err: any, { setError }) => {
      if (err instanceof APIError) {
        mapErrors(setError, err, [
          ["numberOfQuestions"],
          ["curriculum"],
          ["grade"],
          ["description"],
          ["objective"],
        ])
      }
    },
  })
  const generateProTool = useMutation({
    mutationFn: async ({
      data,
    }: {
      data: QuizProForm
      setError: UseFormSetError<QuizProForm>
    }) => {
      setFormData(prevState => {
        if (!prevState) {
          return { proForm: data }
        }

        return { ...prevState, proForm: data }
      })
      setCurrentMode("pro")
      return api.aiTools.quiz.generatePro({ data })
    },
    onSuccess: async res => {
      const { responseId, id } = res
      resetState()
      flushSync(() => {
        setCurrentResponseId(responseId)
        setPromptDataId(id)
        setOverviewData({
          id: res.id,
          numberOfIndividualQuestions: res.numberOfIndividualQuestions,
          curriculum: res.curriculum,
          grade: res.grade,
          topic: res.topic,
          book: res.book,
          difficultyLevel: res.difficultyLevel,
          country: res.country,
          subject: res.subject,
          numberOfComprehensionQuestions: res.numberOfComprehensionQuestions,
          numberOfComprehensions: res.numberOfComprehensions,
          title: `New ${getToolName(ToolType.quizPro)}`,
          isPro: true,
        })
      })

      await streamOutputPro.refetchAsync({
        currentResponseId: responseId,
      })
      await streamTitlePro.refetchAsync({
        currentResponseId: responseId,
      })
      try {
        await overviewAPI(id)
      } catch (error) {
        console.error("Error fetching overview data:", error)
      }
      queryClient.refetchQueries({
        queryKey: queries.aiTools.listHistory(ToolType.quiz).queryKey,
        type: "active",
        exact: true,
      })
    },
    onError: (err: any, { setError }) => {
      if (err instanceof APIError) {
        mapErrors(setError, err, [
          ["grade"],
          ["curriculum"],
          ["numberOfIndividualQuestions"],
          ["difficultyLevel"],
          ["topic"],
          ["country"],
          ["subject"],
          ["numberOfComprehensions"],
          ["numberOfComprehensionQuestions"],
        ])
      }
    },
  })

  const streamTitle = useStreamQuery({
    responseType: StreamResponseEnum.title,
    toolType: ToolType.quiz,
  })

  const streamOutput = useStreamQuery({
    responseType: StreamResponseEnum.generate,
    toolType: ToolType.quiz,
    onError: () => {
      toast.error("Generation Failed")
    },
    onStreaming(chunks) {
      setOutput(prev => (prev += chunks))
    },
  })

  const regenerateStream = useStreamQuery({
    responseType: StreamResponseEnum.regenerate,
    toolType: ToolType.quiz,
    onSuccess: () => {
      toast.success("Re-generated Successfully")
    },
    onError: () => {
      toast.error("Re-generation Failed")
    },
    onStreaming(chunks) {
      setOutput(prev => (prev += chunks))
    },
  })
  const streamTitlePro = useStreamQuery({
    responseType: StreamResponseEnum.title,
    toolType: ToolType.quizPro,
  })

  const streamOutputPro = useStreamQuery({
    responseType: StreamResponseEnum.generate,
    toolType: ToolType.quizPro,
    onError: () => {
      toast.error("Generation Failed")
    },
    onStreaming(chunks) {
      setOutput(prev => (prev += chunks))
    },
  })

  const regenerateStreamPro = useStreamQuery({
    responseType: StreamResponseEnum.regenerate,
    toolType: ToolType.quizPro,
    onSuccess: () => {
      toast.success("Re-generated Successfully")
    },
    onError: () => {
      toast.error("Re-generation Failed")
    },
    onStreaming(chunks) {
      setOutput(prev => (prev += chunks))
    },
  })

  const userVote = useMutation({
    mutationFn: async ({
      reaction,
      isPro,
    }: {
      reaction: boolean | undefined | null
      isPro: boolean
    }) => {
      const eventName = reaction
        ? TrackingEvents.aiToolOutputUpvoted
        : TrackingEvents.aiToolOutputDownvoted
      const toolType = isPro ? ToolType.quizPro : ToolType.quiz

      trackingService.trackEvent(eventName, {
        tool_type: toolType,
      })

      return api.aiTools.quiz.updateData({
        urlParams: { id: currentResponseId!, type: toolType },
        data: {
          isPositiveResponse: reaction,
        },
      })
    },
  })

  const regenerateResponse = useMutation({
    mutationFn: ({ reason }: { reason: string; isPro: boolean }) =>
      api.aiTools.quiz.regenerateResponse({
        urlParams: {
          id: currentResponseId!,
        },
        data: {
          regenerateInstruction: reason,
        },
      }),
    onSuccess: async (res, { isPro, reason }) => {
      flushSync(() => {
        setCurrentResponseId(res.id)
        setOutput("")
      })
      if (isPro) {
        await regenerateStreamPro.refetchAsync({
          currentResponseId: res.id,
          regenReason: reason,
        })
      } else {
        await regenerateStream.refetchAsync({
          currentResponseId: res.id,
          regenReason: reason,
        })
      }
      await overviewAPI(id || promptDataId)
    },
    onError: err => {
      if (err instanceof APIError) {
        if ((err?.statusCode || 0) >= 500) {
          toast.error("We're unable to process your request", {
            description: "Please try again later",
          })
          return
        }
        if (err.errors.message) {
          toast.error(err.errors.message)
          return
        }
      } else {
        toast.error("We're unable to process your request", {
          description: "Please try again later",
        })
      }
    },
  })

  const preferredPromptData = useQuery({
    queryFn: () => api.aiTools.retrievePreferredPromptData(),
    queryKey: ["preferredPromptData"],
  })

  return {
    promptDataId,
    generateTool,
    setPromptDataId,
    currentResponseId,
    setCurrentResponseId: id => {
      setCurrentResponseId(id)
      if (id) {
        getContent(id)
      }
    },
    responseIds,
    setResponseIds,
    stream: {
      ...streamOutput,
      refetchAsync: ({ currentResponseId: id }) =>
        streamOutput.refetchAsync({
          currentResponseId: id,
        }),
    },
    streamPro: {
      ...streamOutputPro,
      refetchAsync: ({ currentResponseId: id }) =>
        streamOutputPro.refetchAsync({
          currentResponseId: id,
        }),
    },

    regenerateResponse,
    overviewData,
    userVote,
    regenerateStream,
    generateProTool,
    output,
    isLoading:
      isLoading ||
      regenerateStream.isLoading ||
      streamOutput.isLoading ||
      regenerateStreamPro.isLoading ||
      streamOutputPro.isLoading,
    isPositiveResponse,
    resetState,
    pageLoading,
    onBack,
    handleCancelApi,
    formData,
    preferredPromptData: preferredPromptData.data ?? null,
    isError:
      currentMode === "pro" ? streamOutputPro.isError : streamOutput.isError,
  }
}
